import axios from 'axios';
import * as mammoth from 'mammoth';
import { getAuthToken } from '../auth/authSession';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:5000/api';

interface ApiErrorShape {
  message?: string;
  success?: boolean;
}

// Create axios instance for profile endpoints
const profileClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to attach auth token
profileClient.interceptors.request.use(
  config => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiErrorShape | undefined;
    return data?.message ?? error.message;
  }
  return 'Request failed';
};

// Type definitions
export interface ProfileUser {
  id: number;
  name: string;
  last_name: string;
  email: string;
  role: string;
  ref_Name_1?: string | null;
  ref_PhoneNumber_1?: string | null;
  ref_Details_1?: string | null;
  ref_Name_2?: string | null;
  ref_PhoneNumber_2?: string | null;
  ref_Details_2?: string | null;
}

export interface LocumProfile {
  user: ProfileUser;
  profile: {
    phone: string;
    qualifications: string;
    experience_years: number;
    location: string;
    travel_distance: number;
    address?: string | null;
    city?: string | null;
    zip_code?: string | null;
    dob?: string | null;
    gender?: 'male' | 'female' | 'other' | null;
    qualification_date?: string | null;
    independent_prescriber?: number | null;
    locum_role: string;
  };
  documents: Array<{
    id: number;
    doc_type: string;
    document_name: string;
  }>;
}

export interface PharmacistProfile {
  user: ProfileUser;
  profile: {
    pharmacy_name: string;
    contact_person: string;
    phone_number?: string | null;
    dispensing_system?: string | null;
    address?: string | null;
    location: string;
    license_number: string;
  };
  documents: Array<{
    id: number;
    doc_type: string;
    document_name: string;
  }>;
}

export type Profile = LocumProfile | PharmacistProfile;

export interface UpdateProfilePayload {
  [key: string]: string | number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Fetch user profile (both locum and pharmacy)
 * @returns Promise with profile data based on user role
 */
export const getProfile = async (): Promise<Profile> => {
  try {
    const { data } = await profileClient.get<ApiResponse<Profile>>('/profile');
    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch profile');
    }
    return data.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

/**
 * Update user profile (both locum and pharmacy)
 * @param payload Profile data to update
 * @returns Promise with updated profile data
 */
export const updateProfile = async (
  payload: UpdateProfilePayload
): Promise<Profile> => {
  try {
    const { data } = await profileClient.put<ApiResponse<unknown>>(
      '/profile',
      payload
    );
    if (!data.success) {
      throw new Error(data.message || 'Failed to update profile');
    }

    // API update response can differ from GET /profile response shape.
    // Always re-fetch and return canonical profile shape used across the UI.
    return await getProfile();
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

/**
 * Download a document file
 * @param filename Name of the document file to download
 */
export const downloadDocument = async (filename: string): Promise<void> => {
  try {
    // Get the base server URL (without /api)
    const baseUrl = API_BASE.replace('/api', '');

    // Create axios instance for file download with blob response
    const fileClient = axios.create({
      baseURL: baseUrl,
      responseType: 'blob'
    });

    // Add auth token to file download request
    fileClient.interceptors.request.use(
      config => {
        const token = getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Fetch the file from root-level uploads folder
    const response = await fileClient.get(`/uploads/documents/${filename}`);

    // response.data is already a Blob when responseType is 'blob'
    const blob = response.data as Blob;

    // Create a temporary download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

/**
 * View a document file in a new tab
 * @param filename Name of the document file to view
 */
export const viewDocument = (filename: string): void => {
  void (async () => {
    const baseUrl = API_BASE.replace('/api', '');
    const extension = filename.split('.').pop()?.toLowerCase();
    const encodedFilename = encodeURIComponent(filename);
    const fileUrl = `${baseUrl}/uploads/documents/${encodedFilename}`;

    if (extension === 'docx') {
      const previewTab = window.open('', '_blank');

      if (!previewTab) {
        throw new Error(
          'Unable to open preview tab. Please allow popups for this site.'
        );
      }

      previewTab.document.write(`
        <html>
          <head>
            <title>Loading ${filename}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 24px; color: #333; }
              .loading { font-size: 16px; color: #555; }
            </style>
          </head>
          <body>
            <p class="loading">Loading document preview...</p>
          </body>
        </html>
      `);
      previewTab.document.close();

      try {
        const token = getAuthToken();
        const response = await axios.get<ArrayBuffer>(fileUrl, {
          responseType: 'arraybuffer',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });

        const { value: html } = await mammoth.convertToHtml({
          arrayBuffer: response.data
        });

        previewTab.document.open();
        previewTab.document.write(`
          <html>
            <head>
              <meta charset="UTF-8" />
              <title>${filename}</title>
              <style>
                body {
                  font-family: Arial, sans-serif;
                  max-width: 960px;
                  margin: 24px auto;
                  padding: 0 16px;
                  line-height: 1.6;
                  color: #222;
                }
                img { max-width: 100%; height: auto; }
              </style>
            </head>
            <body>
              ${html}
            </body>
          </html>
        `);
        previewTab.document.close();
        return;
      } catch (error) {
        previewTab.location.href = fileUrl;
        return;
      }
    }

    window.open(fileUrl, '_blank');
  })();
};

export default profileClient;
